import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  CuentaAbiertaDetalle,
  CuentaAbiertaItem,
  recepcionApi,
} from '../lib/api'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmDialog'

const MONO = { fontFamily: 'JetBrains Mono' } as const

// Etiqueta operativa del estado del cargo (display puro):
// OMITIDO = quedo contabilizado en la cuenta interna v2 (modo actual).
function cargoChip(estado: string): { txt: string; color: string } {
  if (estado === 'OMITIDO') return { txt: 'en cuenta', color: '#2FE08A' }
  if (estado === 'PUSHEADO') return { txt: 'enviado a ERP', color: '#2FE08A' }
  if (estado === 'PENDIENTE_PUSH') return { txt: 'pendiente', color: '#F5A623' }
  return { txt: estado.toLowerCase(), color: '#94A0CD' }
}

export default function RecepcionCuentasAbiertas() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const confirm = useConfirm()

  const [cuentas, setCuentas] = useState<CuentaAbiertaItem[]>([])
  const [seleccionado, setSeleccionado] = useState<CuentaAbiertaDetalle | null>(
    null,
  )
  const [loading, setLoading] = useState(true)
  const [cargandoDetalle, setCargandoDetalle] = useState(false)
  const [cerrando, setCerrando] = useState(false)

  async function refresh() {
    setLoading(true)
    try {
      const data = await recepcionApi.listar()
      setCuentas(data)
    } catch (err: any) {
      showToast(`Error: ${err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  async function cobrarYCerrar(cliente_id: number, nombre: string, total: number) {
    // Irreversible: cierra la cuenta y egresa al paciente. QA 5-jul: se usa el
    // ConfirmDialog estilizado (antes window.confirm nativo, frágil en kiosco/PWA).
    const ok = await confirm({
      title: 'Cobrar y cerrar cuenta',
      message:
        `¿Cobrar y CERRAR la cuenta de ${nombre} por $${total.toFixed(2)}?\n\n` +
        'El paciente quedará egresado y la cuenta ya no podrá modificarse. ' +
        'Esta acción no se puede deshacer.',
      confirmText: `Cobrar $${total.toFixed(2)}`,
      cancelText: 'Cancelar',
      variant: 'danger',
    })
    if (!ok) return
    setCerrando(true)
    try {
      const r = await recepcionApi.cerrarCuenta(cliente_id)
      showToast(
        `Cuenta cerrada: ${r.nombre} · total cobrado $${Number(r.total_cobrado).toFixed(2)}`,
        'success',
      )
      setSeleccionado(null)
      await refresh()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setCerrando(false)
    }
  }

  useEffect(() => {
    refresh()
    const i = setInterval(refresh, 30_000)
    return () => clearInterval(i)
  }, [])

  async function verDetalle(cliente_id: number) {
    setCargandoDetalle(true)
    try {
      const d = await recepcionApi.detalle(cliente_id)
      setSeleccionado(d)
    } catch (err: any) {
      showToast(`Error: ${err.message}`, 'error')
    } finally {
      setCargandoDetalle(false)
    }
  }

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 30 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold leading-tight truncate" style={{ fontFamily: 'Space Grotesk' }}>
            Recepción — Cuentas abiertas
          </h1>
          <p className="text-xs text-[#94A0CD] truncate">{user?.nombre_completo} · se actualiza cada 30 s</p>
        </div>
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>

      <main className="p-4 mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ maxWidth: 1280 }}>
        <section>
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: '#8B97C8' }}>
              Pacientes con saldo ({cuentas.length})
            </h2>
            <button
              onClick={refresh}
              className="text-sm underline"
              style={{ color: '#7FD4FF' }}
            >
              Recargar
            </button>
          </div>
          {loading && <p className="text-gray-500">Cargando...</p>}
          {!loading && cuentas.length === 0 && (
            <div className="bg-white rounded-lg shadow p-6 text-center text-sm">
              <p className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                Sin cuentas abiertas
              </p>
              <p className="text-gray-500">
                Aquí aparece cada paciente con su saldo en vivo cuando se le entrega la primera comida.
              </p>
            </div>
          )}
          {cuentas.map((c) => (
            <button
              key={c.cliente_id}
              onClick={() => verDetalle(c.cliente_id)}
              className="w-full text-left bg-white rounded-lg shadow p-3 mb-2 transition-colors"
              style={{
                border:
                  seleccionado?.cliente_id === c.cliente_id
                    ? '2px solid #006EB4'
                    : '1px solid rgba(140,170,230,.18)',
              }}
            >
              <div className="flex justify-between items-center gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate" style={{ fontFamily: 'Space Grotesk' }}>
                    Hab. <span style={{ ...MONO, color: '#7FD4FF' }}>{c.habitacion}</span> — {c.nombre}
                  </p>
                  <p className="text-xs text-gray-500">
                    {c.comidas_entregadas} comidas · {c.cafeteria_ordenes} cafetería
                  </p>
                </div>
                <p className="text-xl font-bold shrink-0" style={{ ...MONO, color: '#7FD4FF' }}>
                  ${Number(c.total).toFixed(2)}
                </p>
              </div>
              <div className="text-xs text-gray-400 mt-1" style={MONO}>
                Dietas ${Number(c.total_dietas).toFixed(2)} · Café ${Number(c.total_cafeteria).toFixed(2)}
              </div>
            </button>
          ))}
        </section>

        <section className="bg-white rounded-lg shadow p-4">
          {!seleccionado && !cargandoDetalle && (
            <p className="text-gray-500 text-center py-8">
              Selecciona un paciente para ver el detalle de su cuenta.
            </p>
          )}
          {cargandoDetalle && <p className="text-gray-500">Cargando detalle...</p>}
          {seleccionado && (
            <div>
              <h2 className="font-semibold text-lg" style={{ fontFamily: 'Space Grotesk' }}>
                Hab. <span style={{ ...MONO, color: '#7FD4FF' }}>{seleccionado.habitacion}</span> — {seleccionado.nombre}
              </h2>
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(140,170,230,.18)' }}>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#8B97C8' }}>Dietas</p>
                  <p className="font-semibold" style={MONO}>
                    ${Number(seleccionado.total_dietas).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(140,170,230,.18)' }}>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#8B97C8' }}>Cafetería</p>
                  <p className="font-semibold" style={MONO}>
                    ${Number(seleccionado.total_cafeteria).toFixed(2)}
                  </p>
                </div>
                <div className="p-3 rounded-xl text-white" style={{ background: 'linear-gradient(120deg,#006EB4,#143C78)', boxShadow: '0 10px 22px -8px rgba(0,110,180,.6)' }}>
                  <p className="text-[10px] uppercase tracking-wide opacity-80">Total</p>
                  <p className="font-bold" style={MONO}>
                    ${Number(seleccionado.total).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-2" style={{ color: '#8B97C8' }}>
                  Consumos ({seleccionado.items.length})
                </h3>
                <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                  {seleccionado.items.map((it, i) => {
                    const chip = cargoChip(it.cargo_estado)
                    return (
                    <div
                      key={i}
                      className="flex justify-between items-start text-sm border-b py-1.5"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{it.descripcion}</p>
                        <p className="text-xs text-gray-500" style={MONO}>
                          {new Date(it.fecha).toLocaleString()}{' '}
                          <span style={{ color: chip.color }}>· {chip.txt}</span>
                        </p>
                      </div>
                      <div className="text-right shrink-0" style={MONO}>
                        {Number(it.cantidad) !== 1 && (
                          <p className="text-xs text-gray-400">
                            {it.cantidad} × ${Number(it.precio_unit).toFixed(2)}
                          </p>
                        )}
                        <p className="font-semibold">
                          ${Number(it.subtotal).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    )
                  })}
                </div>
              </div>

              <button
                onClick={() =>
                  cobrarYCerrar(
                    seleccionado.cliente_id,
                    seleccionado.nombre,
                    Number(seleccionado.total),
                  )
                }
                disabled={cerrando}
                className="mt-4 w-full py-3 rounded-xl font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(120deg,#006EB4,#143C78)' }}
              >
                {cerrando
                  ? 'Cerrando…'
                  : `Cobrar y cerrar cuenta · $${Number(seleccionado.total).toFixed(2)}`}
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  )
}
